import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Plus, Pencil, Power, Truck, Phone, RotateCcw, Mail, MapPin, ReceiptText, TriangleAlert, CircleCheck, Archive } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUrlString } from '../../hooks/useUrlState';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable, type DataTableColumn } from '../../components/TwDataTable';
import {
  PageHeader, Card, Button, IconButton, Input, Tag, Tooltip, Alert, Popconfirm, Segmented, StatCard, EntityAvatar, message,
} from '../../ui';
import { TransporterFormModal, type TransporterRecord } from './TransporterFormModal';
import { useServerSort } from '../../hooks/useServerSort';

/**
 * THE TRANSPORTER MASTER — the list the pack screen's mandatory carrier dropdown reads.
 *
 * It sits under Warehouse rather than Masters on purpose: the people who add to it are the people
 * dispatching goods, and they should not have to leave their section to record the lorry firm that
 * turned up this morning. Its home is now the Transporters tab of the Warehouses page (`embedded`),
 * next to the locations these carriers collect from; the standalone route stays for the roles that
 * cannot open that page at all — a Warehouse Manager and Accounts hold TRANSPORTER.VIEW but not
 * WAREHOUSE.VIEW, so losing the route would lose them the list.
 */
export function TransportersPage({ embedded }: { embedded?: boolean } = {}) {
  const canManage = usePermission('TRANSPORTER.ADMIN');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TransporterRecord | null>(null);
  const [q, setQ] = useUrlString('q');
  const [status, setStatus] = useUrlString<'active' | 'retired' | 'all'>('status', 'active');
  const [gap, setGap] = useUrlString<'1' | ''>('gap', '');   // only rows nobody can actually ring
  const qc = useQueryClient();

  // The WHOLE master, retired rows included, in one fetch. It is tens of rows, so the status
  // segmented and the search both narrow it here — and the KPI tiles can then count the retired ones
  // while the list is showing only the active ones, instead of collapsing to zero.
  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees
  // the same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['transporters', 'all', sort],
    queryFn: () => request<TransporterRecord[]>({ url: '/transporters', params: { includeInactive: 'true', ...(sort ? { sort } : {}) } }),
  });

  // A carrier with neither a person nor a number on file: pickable at the pack screen, unreachable
  // when the lorry is late. That is a gap worth colouring, not just an empty cell.
  const isReachable = (t: TransporterRecord) => !!(t.mobile || t.contactPerson);

  // Search is client-side: this master is a short list (tens of rows), so a round trip per keystroke
  // would buy nothing. The server filter exists for the pack screen's own lookups.
  const searched = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data ?? [];
    return (data ?? []).filter((t) => [t.name, t.city, t.contactPerson, t.mobile, t.email, t.gstNo, t.notes]
      .some((v) => String(v ?? '').toLowerCase().includes(term)));
  }, [data, q]);

  const rows = useMemo(() => searched
    .filter((t) => (status === 'all' ? true : status === 'retired' ? !t.isActive : !!t.isActive))
    .filter((t) => !gap || !isReachable(t)),
  [searched, status, gap]);

  /* Tiles count the searched set MINUS the two filters they themselves set, so clicking one never
     drops its siblings to zero. Each click REPLACES that pair, so the number on a tile is exactly
     what the table then shows. */
  const kpi = useMemo(() => ({
    all: searched.length,
    active: searched.filter((t) => !!t.isActive).length,
    retired: searched.filter((t) => !t.isActive).length,
    unreachable: searched.filter((t) => !!t.isActive && !isReachable(t)).length,
  }), [searched]);

  const pick = (s: 'active' | 'retired' | 'all', g: '1' | '' = '') => { setStatus(s); setGap(g); };

  const retire = useMutation({
    mutationFn: (id: string) => request({ url: `/transporters/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Transporter retired');
      qc.invalidateQueries({ queryKey: ['transporters'] });
    },
    onError: (e: any) => message.error(
      e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not retire that transporter'),
  });

  const revive = useConfirmedMutation({ title: "Bring this transporter back?", content: "Packing staff can pick them again.", okText: "Yes, restore" }, {
    mutationFn: (t: TransporterRecord) => request({ url: `/transporters/${t.id}`, method: 'PUT', data: { isActive: true } }),
    onSuccess: () => {
      message.success('Transporter is available again');
      qc.invalidateQueries({ queryKey: ['transporters'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not reactivate that transporter'),
  });

  // Truthiness, never `=== false`: the flag is a tinyint at rest, so anything that reaches this page
  // without the entity's boolean transform arrives as 0/1 and a strict test silently reads as "active".
  const isRetired = (t: TransporterRecord) => !t.isActive;

  // Every field the master holds gets its own column: this list is short and wide, and the version that
  // folded contact person + phone into one cell (and notes under the name) left three quarters of the
  // row empty while the details people actually ring were the ones squeezed.
  const dash = <span className="text-subtle">—</span>;
  const columns: DataTableColumn<TransporterRecord>[] = [
    {
      key: 'name', title: 'Transporter', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (v: string, r) => (
        <div className="flex min-w-0 items-center gap-2.5">
          {/* The carrier's own colour AND its initials, both held steady by its name — the anchor a
              column of black text never gave the eye. No fixed `icon=`: a truck on every row is the
              same picture repeated, and the initials of "Heera Thakur" vs "Zed" actually differ. */}
          <EntityAvatar name={v} square size={34} muted={isRetired(r)} />
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <b className={`truncate ${isRetired(r) ? 'opacity-50' : ''}`}>{v}</b>
              {isRetired(r)
                ? <Tag tone="neutral" icon={<Archive size={11} />}>Retired</Tag>
                : <Tag tone="green" icon={<CircleCheck size={11} />}>In service</Tag>}
            </div>
            {!isRetired(r) && !isReachable(r) && (
              <span className="mt-0.5 inline-flex items-center gap-1 type-caption text-warning-600 dark:text-harvest-300">
                <TriangleAlert size={11} /> no one to call
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'contactPerson', title: 'Contact person', dataIndex: 'contactPerson', sortable: true,
      render: (v: string | null, r) => (v
        ? <span className="inline-flex items-center gap-2"><EntityAvatar name={v} size={24} />{v}</span>
        : dash),
    },
    {
      key: 'mobile', title: 'Mobile', dataIndex: 'mobile', sortable: true,
      render: (v: string | null) => (v
        ? (
          <a href={`tel:${v}`} onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 rounded-pill border border-brand-500/25 bg-brand-500/8 px-2 py-0.5 type-label text-brand-700 transition-colors hover:bg-brand-500/15 dark:text-brand-300">
            <Phone size={12} /> {v}
          </a>
        )
        : dash),
    },
    {
      key: 'email', title: 'Email', dataIndex: 'email', sortable: true,
      render: (v: string | null) => (v
        ? (
          <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()}
            className="inline-flex min-w-0 items-center gap-1.5 text-sky-600 underline-offset-2 hover:underline dark:text-sky-400">
            <Mail size={12} className="shrink-0" /> <span className="truncate">{v}</span>
          </a>
        )
        : dash),
    },
    {
      key: 'city', title: 'City / base', dataIndex: 'city', sortable: true,
      render: (v: string | null) => (v
        ? (
          <span className="inline-flex items-center gap-1 rounded-pill bg-surface-3 px-2 py-0.5 type-label text-muted">
            <MapPin size={11} className="text-subtle" /> {v}
          </span>
        )
        : dash),
    },
    {
      key: 'gstNo', title: 'GSTIN', dataIndex: 'gstNo', sortable: true,
      render: (v: string | null) => (v
        ? (
          <span className="inline-flex items-center gap-1.5 type-caption tabular-nums text-content" title={v}>
            <ReceiptText size={12} className="shrink-0 text-violet-500" /> {v}
          </span>
        )
        : dash),
    },
    { key: 'notes', title: 'Notes', dataIndex: 'notes', render: (v: string | null) => (v ? <span className="text-muted">{v}</span> : dash) },
    {
      // Fixed width + nowrap: a date that wraps to "03 Sep / 2026" reads as two values, not one.
      key: 'createdAt', title: 'Added', dataIndex: 'createdAt', sortable: true, align: 'center', width: 116,
      render: (v: string | null) => (v ? <span className="whitespace-nowrap text-muted">{dayjs(v).format('DD MMM YYYY')}</span> : dash),
    },
    ...(canManage ? [{
      key: 'actions', title: '', alwaysVisible: true, width: 92,
      render: (_: any, r: TransporterRecord) => (
        <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
          <Tooltip label="Edit">
            <IconButton diameter={32} aria-label={`Edit ${r.name}`}
              className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
              onClick={() => { setEditing(r); setOpen(true); }}>
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
          {isRetired(r) ? (
            <Tooltip label="Make available again">
              <IconButton diameter={32} aria-label={`Reactivate ${r.name}`}
                className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                onClick={() => revive.mutate(r)}>
                <RotateCcw size={16} />
              </IconButton>
            </Tooltip>
          ) : (
            <Popconfirm
              title={`Retire ${r.name}?`}
              description="They leave the pack dropdown. Shipments they already carried keep their name. Refused while they still hold undelivered goods."
              // The act named in the button, not a bare yes — this one is reversible, but the reader
              // should not have to re-read the title to know what they are agreeing to.
              okText="Yes, retire"
              onConfirm={() => retire.mutate(r.id)}
            >
              <Tooltip label="Retire">
                <IconButton diameter={32} aria-label={`Retire ${r.name}`}
                  className="border border-line text-muted hover:border-danger-300 hover:bg-danger-50 hover:text-danger-700">
                  <Power size={16} />
                </IconButton>
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    } as DataTableColumn<TransporterRecord>] : []),
  ];

  return (
    <div>
      {embedded ? (
        // As a tab the page title would repeat the one above the tab strip, so only the action carries over.
        canManage && (
          <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
              Add transporter
            </Button>
          </div>
        )
      ) : (
        <PageHeader
          title="Transporters"
          subtitle="The carriers goods can be dispatched with. Packing an order requires picking one of these."
          icon={<Truck />}
          actions={canManage ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
              Add transporter
            </Button>
          ) : undefined}
        />
      )}

      <Alert
        className="mb-4"
        type="info"
        title="This list is what the warehouse picks from when packing"
        description="A transporter must be chosen before an order can be packed, so anything missing here blocks a dispatch — which is why the pack screen can also add one on the spot. Retiring a transporter only takes them out of that dropdown; every shipment they carried keeps their name."
      />

      <div className="mb-3.5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          tone="brand" icon={<Truck size={18} />} label="In service" value={data ? kpi.active : '—'}
          hint="pickable when packing an order"
          onClick={kpi.active ? () => pick('active') : undefined} active={status === 'active' && !gap}
        />
        <StatCard
          tone="warning" icon={<TriangleAlert size={18} />} label="No one to call" value={data ? kpi.unreachable : '—'}
          hint={kpi.unreachable ? 'in service, but no contact on file' : 'every carrier is reachable'}
          onClick={kpi.unreachable ? () => pick('active', '1') : undefined} active={gap === '1'}
        />
        <StatCard
          tone="neutral" icon={<Archive size={18} />} label="Retired" value={data ? kpi.retired : '—'}
          hint="out of the pack dropdown; past shipments keep their name"
          onClick={kpi.retired ? () => pick('retired') : undefined} active={status === 'retired'}
        />
        <StatCard
          tone="sky" icon={<ReceiptText size={18} />} label="On the master" value={data ? kpi.all : '—'}
          hint="every carrier, retired included"
          onClick={kpi.all ? () => pick('all') : undefined} active={status === 'all' && !gap}
        />
      </div>

      <Card bodyClassName="p-0 sm:p-0">
        <div className="flex flex-wrap items-center gap-3 p-4 pb-0">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, city, contact or GSTIN" aria-label="Search name, city, contact or GSTIN"
            allowClear
            className="max-w-[320px]"
          />
          <Segmented
            value={status}
            onChange={(v) => { setStatus(v as 'active' | 'retired' | 'all'); setGap(''); }}
            options={[{ value: 'active', label: 'In service' }, { value: 'retired', label: 'Retired' }, { value: 'all', label: 'All' }]}
          />
          {gap === '1' && (
            <Button size="sm" icon={<RotateCcw size={15} />} onClick={() => setGap('')}>Showing carriers with no contact</Button>
          )}
        </div>
        <div className="p-4">
          <TwDataTable<TransporterRecord>
            storageKey="transporters"
            rowKey="id"
            loading={isLoading}
            dataSource={rows}
            columns={columns}
            sortState={sortState}
            onSortChange={onSortChange}
            emptyText="No transporters yet — add the carriers you dispatch with."
            onRow={(r) => ({ onClick: canManage ? () => { setEditing(r); setOpen(true); } : undefined })}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </div>
      </Card>

      <TransporterFormModal
        open={open}
        record={editing}
        onClose={() => { setOpen(false); setEditing(null); }}
      />
    </div>
  );
}
